import { useEffect, useRef, useState } from 'react';
import type { Category } from '@/lib/types';

interface CategoryFilterProps {
  categories: Category[];
  value: string | null;
  onChange: (id: string | null) => void;
  loading?: boolean;
}

export default function CategoryFilter({ categories, value, onChange, loading }: CategoryFilterProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const selected = categories.find((c) => c.id === value) ?? null;
  const label = selected ? selected.name : 'Todas las categorías';

  const select = (id: string | null) => {
    onChange(id);
    setOpen(false);
  };

  const optionClass = (active: boolean) =>
    `w-full flex items-center justify-between gap-2 px-4 py-2 text-sm text-left transition cursor-pointer ${
      active ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-foreground-700 hover:bg-background-100'
    }`;

  return (
    <div ref={containerRef} className="relative w-full sm:w-72">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 transition cursor-pointer"
      >
        <span className="flex items-center gap-2 min-w-0">
          <i className="ri-price-tag-3-line text-foreground-500" />
          <span className="truncate">{label}</span>
        </span>
        <i
          className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="absolute z-20 mt-2 w-full max-h-72 overflow-y-auto rounded-lg border border-background-300 bg-background-50 py-1">
          <button type="button" onClick={() => select(null)} className={optionClass(value === null)}>
            <span className="truncate">Todas las categorías</span>
            {value === null && <i className="ri-check-line text-primary-600" />}
          </button>

          {loading ? (
            <div className="px-4 py-3 text-sm text-foreground-500">Cargando…</div>
          ) : categories.length === 0 ? (
            <div className="px-4 py-3 text-sm text-foreground-500">Sin categorías</div>
          ) : (
            categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => select(category.id)}
                className={optionClass(value === category.id)}
              >
                <span className="truncate">{category.name}</span>
                {value === category.id && <i className="ri-check-line text-primary-600" />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}